import { Link } from 'gatsby'
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useWindowSize } from 'react-use'
import { useLink } from '../../hooks/useLink'
import InlinePlzTool from '../organisms/InlinePlzTool'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { motion, useTransform, useViewportScroll } from 'framer-motion'

const M01HeaderAlternative = ({
  heading,
  copy,
  image,
  roundImages,
  svgImageContent,
  svgImageScript,
  svgImageContentMobile,
  svgImageScriptMobile,
  youtubeId,
  video,
  videoMobile,
  isVideoLooped,
  beginningOfTheVideoLoop,
  moveVideoToTop,
  makeVideoSmaller,
  videoRatio,
  link1,
  link2,
  copy2,
  link3,
  dataTable,
  footerText,
  hasPlzTool,
  dataTable2,
  cardImage,
  cardCopy,
  anchorLinkId,
}) => {
  const { width: windowWidth } = useWindowSize(
    typeof window !== 'undefined' ? window.innerWidth : 1280,
  )
  const isMobile = windowWidth < 768
  const isDesktop = windowWidth >= 1024

  const { label: link1label, url: link1url } = useLink(link1)
  const { label: link2label, url: link2url } = useLink(link2)
  const { label: link3label, url: link3url } = useLink(link3)

  const hasLink1 = link1?.type
  const hasLink2 = link2?.type
  const hasLink3 = link3?.type

  const hasMobileVideo = videoMobile && videoMobile[0]

  const isToggleMoveTop = moveVideoToTop

  let videoRatioDefault = videoRatio || 100
  const [videoIsPlayed, setVideoIsPlayed] = useState(false)

  const [elementTop, setElementTop] = useState(0)
  const [elementHeight, setElementHeight] = useState(0)
  const [parallaxElementHeight, setParallaxElementHeight] = useState(0)
  const ref = useRef(null)
  const parallaxRef = useRef(null)
  const { scrollY } = useViewportScroll()

  const elementsDif = elementHeight - parallaxElementHeight
  const offset = elementsDif <= 0 ? 40 : elementsDif < 100 ? 50 : elementsDif / 2

  const initial = elementTop - 100
  const final = elementTop + 2 * offset

  const y = useTransform(
    scrollY,
    [initial, final],
    [0, isToggleMoveTop ? offset : 2 * offset],
  )

  // const y = useSpring(yRange, { stiffness: 800, damping: 90 });

  useLayoutEffect(() => {
    const onResize = () => {
      const element = ref.current
      const parallax = parallaxRef.current

      if (isDesktop) {
        setElementTop(
          element.getBoundingClientRect().top + window?.scrollY,
        )
        setElementHeight(element.getBoundingClientRect().height)
        setParallaxElementHeight(parallax.getBoundingClientRect().height)
      } else {
        setElementHeight(0)
        setParallaxElementHeight(0)
      }
    }

    onResize()
  }, [ref, windowWidth, isDesktop])

  const onPlayVideo = el => {
    setVideoIsPlayed(true)
  }

  const onVideoEnded = ev => {
    if (
      !isVideoLooped &&
      beginningOfTheVideoLoop &&
      beginningOfTheVideoLoop >= 0
    ) {
      ev.target.currentTime = beginningOfTheVideoLoop
      ev.target.play()
    }
  }

  useEffect(() => {
    if (svgImageScript) {
      const executeScript = new Function(svgImageScript)
      executeScript()
    }

    if (svgImageScriptMobile) {
      const executeScriptMobile = new Function(svgImageScriptMobile)
      executeScriptMobile()
    }
  }, [svgImageScript, svgImageScriptMobile])

  return (
    <div className="container" id={anchorLinkId}>
      <div className="row-type-f">
        <div className="z-10 col-1-type-f" ref={parallaxRef}>
          <motion.div
            className="z-10 w-full pr-10 mt-0 lg:-mt-11 lg:mt-0"
            style={{ y }}
          >
            <h1 className="mb-4 xl:mb-5 u-h0">{heading}</h1>
            <div className="mb-8 column-indent u-p">
              <div
                className="link-wrapper"
                dangerouslySetInnerHTML={{ __html: copy }}
              />
              {(hasLink1 || hasLink2) && (
                <div className="mt-6 space-y-4">
                  {hasLink1 && (
                    <div>
                      <Link to={link1url} className="u-btn">
                        {link1label}
                      </Link>
                    </div>
                  )}
                  {hasLink2 && (
                    <div>
                      <Link to={link2url} className="u-btn">
                        {link2label}
                      </Link>
                    </div>
                  )}
                </div>
              )}
              {copy2 && (
                <div
                  className="mt-4 link-wrapper"
                  dangerouslySetInnerHTML={{ __html: copy2 }}
                />
              )}
              {hasLink3 && (
                <div className="mt-5 xl:mt-6">
                  <Link to={link3url} className="u-btn">
                    {link3label}
                  </Link>
                </div>
              )}
            </div>

            {dataTable && (
              <div className="max-w-full mb-8 divide-y column-indent divide-gray-4">
                {dataTable.map(({ icon, text }) => (
                  <div
                    key={text}
                    className="flex items-center justify-start py-2.5"
                  >
                    <img src={icon[0]?.url} alt="" className="mr-1.5" />{' '}
                    <div>{text}</div>
                  </div>
                ))}
              </div>
            )}
            {footerText && (
              <div
                className={`${
                  hasPlzTool ? 'mb-4' : 'mb-8'
                } column-indent link-wrapper`}
                dangerouslySetInnerHTML={{ __html: footerText }}
              />
            )}
            {hasPlzTool && (
              <div className="mb-8 column-indent">
                <QueryClientProvider client={new QueryClient()}>
                  <InlinePlzTool />
                </QueryClientProvider>
              </div>
            )}
            {dataTable2 && (
              <div className="max-w-full mb-10 divide-y column-indent divide-gray-4">
                {dataTable2.map(({ icon, text }) => (
                  <div
                    key={text}
                    className="flex items-center justify-start py-2.5"
                  >
                    <img src={icon[0]?.url} alt="" className="mr-1.5" />{' '}
                    <div>{text}</div>
                  </div>
                ))}
              </div>
            )}
            {cardImage && cardCopy && (
              <div className="flex items-center w-full px-6 py-3 space-x-5 bg-white rounded-[5px] ml-5 shadow-box">
                <img
                  src={cardImage[0]?.url}
                  alt=""
                  className="w-auto h-[90px] flex-shrink-0 "
                />
                <div
                  className="u-p-small link-wrapper"
                  dangerouslySetInnerHTML={{ __html: cardCopy }}
                />
              </div>
            )}
          </motion.div>
        </div>
        <div
          ref={ref}
          className={`col-2-type-f sm:mx-auto ${
            isToggleMoveTop && !makeVideoSmaller
              ? '-mt-16 sm:-mt-36 lg:-mt-36'
              : ''
          } ${
            isToggleMoveTop && makeVideoSmaller
              ? '-mt-20 sm:-mt-16 md:-mt-16 lg:-mt-32'
              : ''
          } ${
            makeVideoSmaller
              ? 'mb-11 md:mb-12 lg:mb-0 px-7 sm:px-0 lg:px-7 xl:px-16'
              : ''
          }`}
        >
          <div className="-mx-[26px] sm:mx-0">
            {image && (
              <img
                src={image[0]?.url}
                alt=""
                className={`w-full h-auto ${
                  roundImages ? 'rounded shadow-tpmp lg:shadow-dp' : ''
                }`}
              />
            )}
            {svgImageContent && (
              <>
                <div
                  className="hidden w-full u-has-svg md:block"
                  dangerouslySetInnerHTML={{ __html: svgImageContent }}
                ></div>
              </>
            )}
            {svgImageContentMobile && (
              <div
                className="block w-full u-has-svg md:hidden"
                dangerouslySetInnerHTML={{ __html: svgImageContentMobile }}
              ></div>
            )}
            {youtubeId && (
              <div className="block">
                <iframe
                  src={`https://www.youtube.com/embed/${youtubeId}?modestbranding=1&rel=0&controls=0&showinfo=0`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full aspect-video rounded-[5px] shadow-tpmp lg:shadow-dp"
                ></iframe>
              </div>
            )}
            {video && video[0] && (
              <div className={`relative`}>
                {isMobile && hasMobileVideo ? (
                  <motion.video
                    width="100%"
                    height="auto"
                    animate={{ opacity: videoIsPlayed ? 1 : 0 }}
                    muted
                    playsInline
                    autoPlay="autoplay"
                    loop={isVideoLooped}
                    onPlay={onPlayVideo}
                    onEnded={onVideoEnded}
                    className={`absolute top-0 left-0 object-cover rounded opacity-0`}
                  >
                    <source
                      src={
                        videoMobile.find(({ extension }) => extension === 'mp4')
                          ?.url
                      }
                      type='video/mp4; codecs="hvc1"'
                    />
                    <source
                      src={
                        videoMobile.find(
                          ({ extension }) => extension === 'webm',
                        )?.url
                      }
                      type="video/webm"
                    />
                  </motion.video>
                ) : (
                  <motion.video
                    width="100%"
                    height="auto"
                    animate={{ opacity: videoIsPlayed ? 1 : 0 }}
                    muted
                    playsInline
                    autoPlay="autoplay"
                    loop={isVideoLooped}
                    onPlay={onPlayVideo}
                    onEnded={onVideoEnded}
                    className={`absolute top-0 left-0 object-cover rounded opacity-0`}
                  >
                    <source
                      src={
                        video.find(({ extension }) => extension === 'mp4')?.url
                      }
                      type='video/mp4; codecs="hvc1"'
                    />
                    <source
                      src={
                        video.find(({ extension }) => extension === 'webm')?.url
                      }
                      type="video/webm"
                    />
                  </motion.video>
                )}
                <span
                  style={{ paddingBottom: `${videoRatioDefault}%` }}
                  className="block w-full bg-gray-1"
                ></span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default M01HeaderAlternative
